import { Head, router, usePage, Link } from '@inertiajs/react';
import { useMemo, useState } from 'react';
import Layout from '@/layouts/Layout';
import { Heading } from '@/components/ui/heading';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

type PageProps = {
  therapist: { email: string };
  submitUrl: string;
  backUrl: string;
  minimumPasswordLength?: number | null;
  confirmablePending?: boolean;
  unconfirmedEmail?: string | null;
  errors?: Record<string, unknown>;
  flash?: { notice?: string; alert?: string; success?: string; error?: string };
};

function normalizeErrors(errors: unknown): string[] {
  if (!errors) return [];
  if (Array.isArray(errors)) return errors.map(String);
  if (typeof errors === 'string') return [errors];
  if (typeof errors === 'object' && errors !== null) {
    const errObj = errors as Record<string, unknown>;
    if (Array.isArray(errObj.full_messages)) return errObj.full_messages.map(String);
    return Object.values(errObj).flat().map(String);
  }
  return [];
}

export default function TherapistAccountEdit() {
  const { props } = usePage<PageProps>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState(props.therapist.email ?? '');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');

  const errorList = useMemo(() => normalizeErrors(props.errors), [props.errors]);

  return (
    <Layout
      isAdmin
      mainHeader={
        <Heading
          level={2}
          description="Aggiorna le credenziali del tuo account."
          wrapperClassName="mb-4"
        >
          Impostazioni account
        </Heading>
      }
    >
      <Head title="Impostazioni account" />

      <div className="w-full">
        <Card>
          <CardHeader>
            <CardTitle>Credenziali</CardTitle>
          </CardHeader>
          <CardContent>
            {props.confirmablePending && props.unconfirmedEmail ? (
              <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                In attesa di conferma per: {props.unconfirmedEmail}
              </div>
            ) : null}

            {errorList.length > 0 ? (
              <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                <ul className="list-disc space-y-1 pl-5">
                  {errorList.map((err) => (
                    <li key={err}>{err}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                setIsSubmitting(true);
                router.patch(
                  props.submitUrl,
                  {
                    therapist: {
                      email,
                      password,
                      password_confirmation: passwordConfirmation,
                      current_password: currentPassword,
                    },
                  },
                  {
                    preserveScroll: true,
                    onFinish: () => setIsSubmitting(false),
                  },
                );
              }}
            >
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-baseline justify-between gap-4">
                  <Label htmlFor="password">Nuova password</Label>
                  <span className="text-xs text-muted-foreground">
                    Lascia vuoto se non vuoi cambiarla
                  </span>
                </div>
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                {props.minimumPasswordLength ? (
                  <p className="text-xs text-muted-foreground">
                    Minimo {props.minimumPasswordLength} caratteri
                  </p>
                ) : null}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password_confirmation">Conferma password</Label>
                <Input
                  id="password_confirmation"
                  type="password"
                  autoComplete="new-password"
                  value={passwordConfirmation}
                  onChange={(e) => setPasswordConfirmation(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-baseline justify-between gap-4">
                  <Label htmlFor="current_password">Password attuale</Label>
                  <span className="text-xs text-muted-foreground">
                    Serve per confermare le modifiche
                  </span>
                </div>
                <Input
                  id="current_password"
                  type="password"
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
              </div>

              <div className="flex flex-wrap items-center gap-2 pt-2">
                <Button type="submit" disabled={isSubmitting}>
                  Salva
                </Button>
                <Button asChild variant="outline" disabled={isSubmitting}>
                  <Link href={props.backUrl}>Indietro</Link>
                </Button>

                <div className="flex-1" />
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
